/**
 * ManageDashboardFranchiseesDialog — HQ-only toggle list that lets
 * Sandra / Sam hide or restore individual franchisees from the
 * Dashboard's "New franchisees" journey overview without opening
 * each profile.
 *
 * Only visibility on the Dashboard is affected. Hidden rows keep
 * their franchisee record, portal access, contracts and full journey
 * tracker untouched (filter honoured by
 * ``mojo_journey_routes.py::dashboard_summary``).
 *
 * Data source:
 *   GET  /api/franchisees            (admin list — includes
 *                                     ``hide_from_dashboard``)
 * Mutation:
 *   PATCH /api/franchisees/{id}/dashboard-visibility {hide: boolean}
 *   The flag is persisted server-side, so toggles survive refresh
 *   automatically — no local storage, no race window.
 *
 * Eligibility filter mirrors the Dashboard query:
 *   - archived_at empty/null,
 *   - lifecycle_status NOT in {ex_franchisee, reverted},
 *   - converted_from_contact_id set (the Discovery → Launch portal
 *     flow writes this; historical imports leave it empty and are
 *     deliberately hidden from the Dashboard anyway).
 *
 * Props:
 *   open:    boolean
 *   onOpenChange(open:boolean)
 *   onChanged(): called once per visible-state flip so the parent
 *                can refetch dashboard-summary and reflect the
 *                change in the five-slot overview.
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2, EyeOff, Eye, Search } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

function isEligible(f) {
  // Already hidden by HQ → always surface in Manage so the toggle is
  // reachable to restore the row, even if the row would otherwise be
  // excluded by the Dashboard-eligibility rules below.
  if (f.hide_from_dashboard) return true;
  if (f.archived_at) return false;
  const status = (f.lifecycle_status || "").toLowerCase();
  if (status === "ex_franchisee" || status === "reverted") return false;
  if (!f.converted_from_contact_id) return false;
  return true;
}

export default function ManageDashboardFranchiseesDialog({ open, onOpenChange, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    api
      .get("/franchisees", { params: { sort_by: "last_name", sort_dir: 1, limit: 500 } })
      .then((res) => {
        if (cancelled) return;
        const list = (res.data?.items || []).filter(isEligible);
        setItems(list);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err?.response?.data?.detail || "Couldn't load franchisees");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((f) => {
      const name = `${f.first_name || ""} ${f.last_name || ""}`.toLowerCase();
      const org = (f.organisation || "").toLowerCase();
      return name.includes(q) || org.includes(q);
    });
  }, [items, query]);

  const toggleHide = async (f) => {
    const next = !f.hide_from_dashboard;
    setBusyId(f.id);
    try {
      const res = await api.patch(`/franchisees/${f.id}/dashboard-visibility`, { hide: next });
      const fresh = res.data?.franchisee || null;
      setItems((prev) => prev.map((row) => (row.id === f.id ? { ...row, ...(fresh || { hide_from_dashboard: next }) } : row)));
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update visibility");
    } finally {
      setBusyId(null);
    }
  };

  const hiddenCount = items.filter((f) => f.hide_from_dashboard).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] flex flex-col"
        data-testid="dashboard-manage-franchisees-dialog"
      >
        <DialogHeader>
          <DialogTitle>Manage "New franchisees"</DialogTitle>
          <DialogDescription>
            Hide a franchisee from the Dashboard's five-slot overview without affecting
            their record, portal access or journey tracker. Changes save automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="relative my-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or territory…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-stone-200 bg-white text-sm focus:border-stone-400 focus:outline-none"
            data-testid="dashboard-manage-franchisees-search"
          />
        </div>

        <div className="flex-1 overflow-y-auto -mx-2 px-2 border-y border-stone-100">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-stone-500 gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading franchisees…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-stone-500 px-4 py-6">No franchisees match that search.</div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {filtered.map((f) => {
                const displayName = `${f.first_name || ""} ${f.last_name || ""}`.trim() || f.organisation || "(no name)";
                const hidden = !!f.hide_from_dashboard;
                return (
                  <li
                    key={f.id}
                    className="flex items-center justify-between py-2.5"
                    data-testid={`dashboard-manage-franchisees-row-${f.id}`}
                  >
                    <div className="min-w-0 pr-3">
                      <div className="font-semibold text-stone-900 truncate flex items-center gap-2">
                        {displayName}
                        {hidden && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-stone-500 bg-stone-100 rounded-full px-2 py-0.5">
                            <EyeOff className="w-3 h-3" /> Hidden
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-stone-500 truncate">
                        {f.organisation || "—"}{f.franchise_number ? ` · ${f.franchise_number}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500">
                        {hidden ? "Hidden" : "Visible"}
                      </span>
                      <Switch
                        checked={!hidden}
                        disabled={busyId === f.id}
                        onCheckedChange={() => toggleHide(f)}
                        data-testid={`dashboard-manage-franchisees-toggle-${f.id}`}
                        aria-label={hidden ? `Show ${displayName} on the Dashboard` : `Hide ${displayName} from the Dashboard`}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter className="pt-2 sm:justify-between">
          <div className="text-xs text-stone-500 self-center">
            {hiddenCount > 0 ? `${hiddenCount} hidden` : "All eligible franchisees visible"}
          </div>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="dashboard-manage-franchisees-done"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
